import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Store } from '@ngrx/store';

import { fromEvent, Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { v4 as uuidv4 } from 'uuid';

import { AppState } from 'src/app/state';
import { initializeUpload, uploadRequest, uploadReset } from 'src/app/dashboard/state/upload/upload.actions';
import {
  selectCompleted,
  selectInProgress,
  selectProgress,
  selectUploaded,
} from '../../../dashboard/state/upload/upload.selectors';
import { UploadIds, UploadKeys } from '../../../models/upload';
import { environment } from 'src/environments/environment';
import { IMAGE_SIZES } from 'src/app/core/dashboard-constants';
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";

const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];
const MAX_IMAGE_FILE_SIZE_MB = 3;
const MIN_IMG_HEIGHT = 360;
const MIN_IMG_WIDTH = 640;
const RECOMMENDED_IMG_SIZE = 1500;
const MAX_IMG_SIZE = 2000;

@Component({
  selector: 'dashboard-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true,
    },
  ],
})
export class ImageUploadComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() uuid: string;
  @Input() label: string;
  @Input() isEdit: boolean;
  @Input() isImageToEdit: boolean;
  @Input() imgPath: string;
  @Input() keyForUpload: UploadKeys;
  @Input() idsForUpload: UploadIds;
  @Input() size: string;
  @Input() autoUpload = true;

  form: FormGroup;
  subscriptions = new Subscription();

  imageFile: File = null;

  publicUrl = environment.publicUrl;

  uploadCompleted$: Observable<boolean>;
  uploadProgress$: Observable<number>;
  uploadIsInProgress$: Observable<boolean>;
  uploadedFiles$: Observable<File[] | string[]>;
  uploadedFiles: File[] | string[];
  safeImageUrl: SafeResourceUrl;
  tempImageUrl: string;

  IMAGE_SIZES = IMAGE_SIZES;

  constructor(private formBuilder: FormBuilder, private store: Store<AppState>, private toastrService: ToastrService, private domSanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      imagePath: [''],
    });

    if (!this.uuid) {
      this.uuid = uuidv4();
    }

    this.store.dispatch(initializeUpload({ uuid: this.uuid }));

    this.uploadCompleted$ = this.store.select(selectCompleted(this.uuid));
    this.uploadProgress$ = this.store.select(selectProgress(this.uuid));
    this.uploadIsInProgress$ = this.store.select(selectInProgress(this.uuid));
    this.uploadedFiles$ = this.store.select(selectUploaded(this.uuid));
    this.subscriptions.add(
      this.uploadedFiles$.subscribe((uploaded) => {
        let value;
        if (uploaded && uploaded.length > 0) {
          this.uploadedFiles = uploaded;
          value = this.isImageToEdit ? this.imgPath : this.uploadedFiles[0];
        }
        if (uploaded && uploaded.length === 0) {
          value = null;
        }
        this.onChange(value);
      })
    );
  }

  get value(): string {
    return this.form.value;
  }

  get imagePath(): AbstractControl {
    return this.form.controls.imagePath;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn) {
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.form.setValue({ imagePath: value }, { emitEvent: false });
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  handleFileInput(event) {
    this.imageFile = event.target?.files[0];

    if (!this.imageFile) {
      return;
    }

    this.subscriptions.add(
      this.isImageValid()
        .pipe(
          filter((valid) => valid),
          tap(() => {
            const extension = this.imageFile.name.split('.').pop();

            if (!SUPPORTED_IMAGE_EXTENSIONS.includes(extension)) {
              this.toastrService.error(
                `Please select a file with one of the following extensions: ${SUPPORTED_IMAGE_EXTENSIONS.toString()}`
              );
              this.imageFile = null;
              return;
            }

            if (this.imageFile.size > MAX_IMAGE_FILE_SIZE_MB * 1024 * 1024) {
              this.toastrService.error(`Your file is too big. The size should be max ${MAX_IMAGE_FILE_SIZE_MB}MB.`);
              this.imageFile = null;
              return;
            }

            if (this.autoUpload) {
              this.store.dispatch(
                uploadRequest({
                  uuid: this.uuid,
                  file: this.imageFile,
                  keyForUpload: this.keyForUpload,
                  idsForUpload: this.idsForUpload,
                })
              );
            } else {
              this.safeImageUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.tempImageUrl);
              this.onChange(this.tempImageUrl);
            }
          }),
          tap(() => (event.target.value = ''))
        )
        .subscribe()
    );
  }

  resetImage(): void {
    if (this.isEdit) {
      this.isImageToEdit = false;
    }
    URL.revokeObjectURL(this.tempImageUrl);
    this.tempImageUrl = null;
    this.store.dispatch(uploadReset({ uuid: this.uuid }));
  }

  isImageValid(): Observable<boolean> {
    const image = new Image();
    image.src = URL.createObjectURL(this.imageFile);
    this.tempImageUrl = image.src;

    return fromEvent<Event & { path: HTMLImageElement[] | Event }>(image, 'load').pipe(
      map((imageUploadEvent) => {
        const path = imageUploadEvent.path || (imageUploadEvent.composedPath && imageUploadEvent.composedPath());
        if (!path) {
          return false;
        }
        const imgHeight = path[0].naturalHeight || 0;
        const imgWidth = path[0].naturalWidth || 0;

        if (imgHeight < MIN_IMG_HEIGHT || imgWidth < MIN_IMG_WIDTH) {
          this.toastrService.error(
            `Your file dimensions are too small. Height should be min ${MIN_IMG_HEIGHT}px, and width should be min ${MIN_IMG_WIDTH}px.`
          );
          return false;
        }

        if (imgHeight > MAX_IMG_SIZE || imgWidth > MAX_IMG_SIZE) {
          this.toastrService.error(`Your file dimensions are too large, max is ${MAX_IMG_SIZE}px.`);
          return false;
        }

        if (imgHeight < RECOMMENDED_IMG_SIZE || imgWidth < RECOMMENDED_IMG_SIZE) {
          this.toastrService.info(
            `It is recommended that file dimensions are min ${RECOMMENDED_IMG_SIZE}px, so that images look crisp on smartphones with high-PPI “retina” screens.`
          );
        }

        return true;
      })
    );
  }

  get supportedImageExtensions() {
    const extensions = [];
    SUPPORTED_IMAGE_EXTENSIONS.forEach((extension) => {
      extensions.push(`image/${extension}`);
    });
    return extensions;
  }

  ngOnDestroy() {
    this.resetImage();
    this.subscriptions.unsubscribe();
  }
}
